import styled, { StyledComponent } from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { callAllHandlers, useForkRef } from "@smartsheet/lodestar-utils";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import React, {
    forwardRef,
    ForwardRefExoticComponent,
    HTMLAttributes,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTheme } from "../../theme/utils/useTheme";
import { TabMenu } from "./TabMenu";
import { TabUnderlineProps, TabUnderline as DefaultTabUnderline } from "./TabUnderline";
import { useTabsContext } from "./TabsContext";
import { tabsTheme } from "./tabsTheme";

interface TabsContainerProps {
    themeMode: RequiredThemeKeys;
}

const TabsContainer = styled("div")<TabsContainerProps>`
    display: inline-flex;
    height: ${({ themeMode }) => tabsTheme.containerHeight(themeMode)};
    // Adding relative to allow absolutely-positioned Underlines.
    // This also creates a stacking context for the tabs/underline without using a z-index.
    position: relative;
    isolation: isolate;
    white-space: nowrap;
`;

const WidthTracker = styled("div")`
    display: inline-flex;
    height: 1px;
    opacity: 0;
    position: absolute;
    bottom: 0;
`;

const HiddenChildrenWrapper = styled("div")`
    position: absolute;
    bottom: 0;
    left: 0;
    opacity: 0;
    z-index: -99999;
    display: flex;
    pointer-events: none;
`;

export interface TabListProps extends HTMLAttributes<HTMLElement> {
    /**
     * Threshold for intersection observer to determine if the tabs should be collapsed.
     * https://developer.mozilla.org/docs/Web/API/IntersectionObserver/thresholds
     */
    collapseThreshold?: number;
    /**
     * Overrides allowing customization of components internal to this component.
     */
    components?: {
        TabUnderline?:
            | ForwardRefExoticComponent<TabUnderlineProps>
            // eslint-disable-next-line @typescript-eslint/ban-types
            | StyledComponent<TabUnderlineProps, object, object>;
    };
    /**
     * Additional options for the IntersectionObserver.
     */
    otherIntersectionOptions?: IntersectionObserverInit;
    /**
     * If true, the tabs will not collapse when the threshold is met.
     * @deprecated This prop will change to the more intuitive `shouldCollapse` in the next major version.
     */
    shouldNotCollapse?: boolean;
}

export const TabList = forwardRef<HTMLDivElement, TabListProps>(
    (
        {
            children,
            components = {},
            collapseThreshold = 0.99,
            otherIntersectionOptions,
            shouldNotCollapse = true,
            ...rest
        },
        providedRef
    ) => {
        const localRef = useRef<HTMLDivElement>(null);
        const ref = useForkRef(localRef, providedRef);
        const widthTrackerRef = useRef<HTMLDivElement>(null);
        const hiddenChildrenWrapperRef = useRef<HTMLDivElement>(null);

        const { renderOn, tabRegistrations, selectedIndex, isCollapsed, setIsCollapsed } = useTabsContext();

        const [reRegisterRefCount, setReRegisterRefCount] = useState(0);
        const [tabListWidth, setTabListWidth] = useState(0);

        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        const underlineShouldTransitionRef = useRef(false);
        const showTransitions = useCallback(() => {
            // We only want to start transitioning on the 1st hover or selection.
            // We don't want to transtition on mount or the bar grows from 0
            // to whatever the selected item's width is.
            // Use a ref since changing this shouldn't cause a re-render by itself,
            // but it will be used in subsequent renders.
            underlineShouldTransitionRef.current = true;
        }, []);

        const tabRefs = useMemo(
            () => tabRegistrations.map(({ tabRef }) => tabRef?.current).filter(Boolean) as HTMLElement[],
            [tabRegistrations]
        );

        const selectedSliderCoords = useUnderline(tabRefs[selectedIndex]);
        const [tabWidths, setTabWidths] = useState<number[]>([]);

        useEffect(() => {
            //increment the reRegisterRefCount to force the tabs to re-register
            //when shouldRenderCollapsed changes more than once a bool is flipped
            //bool logic ---> reRegisterRefCount > 1
            //causing the Tab component to fire its registerTab function
            //You can't register a tab at this level because it causes an infinite loop.
            setReRegisterRefCount(reRegisterRefCount + 1);
        }, [isCollapsed]); //eslint-disable-line react-hooks/exhaustive-deps

        //use ResizeObserver to update the tabWidths when the tabs change size
        //this is an uncommon code path, other than the initial render.
        useEffect(() => {
            //bail if there is no ResizeObserver or no refs set yet
            if (tabRefs.length === 0 || !window.ResizeObserver) return;
            //here we have the tabRefs set and can read their widths
            setTabWidths(tabRefs.map((tabRef) => tabRef.offsetWidth));
            let observer = new ResizeObserver(() => {});

            const resizeCallback = () => {
                //when a resize happens update the tabWidths
                setTabWidths(tabRefs.map((tabRef) => tabRef.offsetWidth));
            };
            tabRefs.forEach((tabRef) => {
                observer = new ResizeObserver(resizeCallback);
                observer.observe(tabRef);
            });

            //cleans up the observer on unmount
            return () => {
                observer.disconnect();
            };
        }, [tabRefs]);

        useEffect(() => {
            if (hiddenChildrenWrapperRef.current) {
                setTabListWidth(hiddenChildrenWrapperRef.current.offsetWidth);
            }
        }, [hiddenChildrenWrapperRef?.current?.offsetWidth]);

        useEffect(() => {
            // we want to skip this effect's code if: there's no intersection observer, the collapse behavior is turned off,
            // or if any of the necessary refs aren't set yet, since this hook is
            // concerned with deciding if the collapsed state should be shown.
            if (
                shouldNotCollapse ||
                !window.IntersectionObserver ||
                !widthTrackerRef.current ||
                !hiddenChildrenWrapperRef.current
            )
                return;
            const options = {
                threshold: collapseThreshold,
                ...otherIntersectionOptions,
            };

            const intersectionObserver = new IntersectionObserver((entries) => {
                //set the state that renders the condensed tabs
                if (entries[0].intersectionRatio <= collapseThreshold) {
                    setIsCollapsed(true);
                } else {
                    setIsCollapsed(false);
                }
            }, options);

            intersectionObserver.observe(widthTrackerRef.current);
            return () => {
                intersectionObserver.disconnect();
            };
        }, [shouldNotCollapse, collapseThreshold, otherIntersectionOptions, setIsCollapsed]);

        // when tabWidths change set the hover and selectedSliderCoords to the new values
        useEffect(() => {
            //if collapsed ignore this effect's code
            if (isCollapsed) return;

            //bail if there are no refs set yet
            if (tabRefs.length === 0 || selectedIndex < 0 || tabRefs.length < selectedIndex - 1) return;

            setHoveredSliderCoordsCb(selectedIndex);
            selectedSliderCoords.width = tabWidths[selectedIndex];
            selectedSliderCoords.left = tabRefs[selectedIndex]?.offsetLeft;
            //eslint-disable-next-line react-hooks/exhaustive-deps
        }, [tabWidths, isCollapsed]);

        const [hoveredSliderCoords, setHoveredSliderCoords] = useState({
            left: 0,
            width: 0,
        });

        const setHoveredSliderCoordsCb = useCallback(
            (hoveredIndex: number) => {
                const hoveredTabRef = tabRefs[hoveredIndex];
                if (hoveredTabRef) {
                    const left = hoveredTabRef.offsetLeft;
                    const width = hoveredTabRef.offsetWidth;
                    setHoveredSliderCoords({ left, width });
                }
            },
            [tabRefs]
        );

        useEffect(() => {
            setHoveredSliderCoordsCb(selectedIndex);
        }, [selectedIndex, setHoveredSliderCoordsCb, themeMode]);

        // Used to hide the hover slider for a tick to re-mount it back at the selected
        const [showHoverUnderline, setShowHoverUnderline] = useState(true);
        const moveHoverSliderBackToSelectedTab = (): void => {
            setShowHoverUnderline(false);
            setHoveredSliderCoordsCb(selectedIndex);
            setTimeout(() => {
                setShowHoverUnderline(true);
            }, 0);
        };

        const Underline = components.TabUnderline || DefaultTabUnderline;
        if (!Underline.displayName) {
            Underline.displayName = DefaultTabUnderline.displayName;
        }

        const tabChildren = useMemo(() => {
            let skippedChildren = 0;
            return React.Children.map(children, (child, realChildIndex) => {
                if (!React.isValidElement(child)) {
                    skippedChildren++;
                    return child;
                }
                const childIndex = realChildIndex - skippedChildren;

                const props = child.props as HTMLAttributes<HTMLElement>;

                return React.cloneElement(child, {
                    onMouseEnter: callAllHandlers(() => {
                        setHoveredSliderCoordsCb(childIndex);
                    }, props.onMouseEnter),
                    onClick: callAllHandlers(() => {
                        if (childIndex === selectedIndex) {
                            return;
                        }
                        showTransitions();
                    }, props.onClick),
                    reRegisterRef: reRegisterRefCount > 1,
                });
            });
        }, [children, selectedIndex, showTransitions, setHoveredSliderCoordsCb, reRegisterRefCount]);

        return (
            <TabsContainer
                {...rest}
                ref={ref}
                role="tablist"
                themeMode={themeMode}
                // Note: onFocus bubbles in React, not in the DOM spec.
                // We don't care about focusing THIS element, but instead care about the
                // children receiving focus to start animations.
                onFocus={callAllHandlers(() => {
                    showTransitions();
                }, rest.onFocus)}
                onMouseEnter={callAllHandlers(() => {
                    showTransitions();
                }, rest.onMouseEnter)}
                onMouseLeave={callAllHandlers(() => {
                    moveHoverSliderBackToSelectedTab();
                }, rest.onMouseLeave)}
            >
                <WidthTracker style={{ width: tabListWidth }} ref={widthTrackerRef} />
                {isCollapsed ? (
                    <TabMenu themeMode={themeMode} renderOn={renderOn} selected>
                        {children}
                    </TabMenu>
                ) : (
                    <>
                        {tabChildren}
                        {!shouldNotCollapse && (
                            <HiddenChildrenWrapper ref={hiddenChildrenWrapperRef}>{tabChildren}</HiddenChildrenWrapper>
                        )}
                    </>
                )}
                {!isCollapsed && (
                    <>
                        <Underline
                            leftPx={0}
                            widthPlusUnit={"100%"}
                            backgroundColor={tabsTheme[renderOn].underlineColor(themeMode)}
                            height={tabsTheme.underlineHeight.default(themeMode)}
                        />
                        {showHoverUnderline && (
                            <Underline
                                leftPx={hoveredSliderCoords.left}
                                widthPlusUnit={hoveredSliderCoords.width + "px"}
                                backgroundColor={tabsTheme[renderOn].underlineHoverColor(themeMode)}
                                underlineTransition={underlineShouldTransitionRef.current}
                                height={tabsTheme.underlineHeight.active(themeMode)}
                            />
                        )}
                        <Underline
                            leftPx={selectedSliderCoords.left}
                            widthPlusUnit={selectedSliderCoords.width + "px"}
                            backgroundColor={tabsTheme.underlineSelectedColor(themeMode)}
                            underlineTransition={underlineShouldTransitionRef.current}
                            height={tabsTheme.underlineHeight.active(themeMode)}
                        />
                    </>
                )}
            </TabsContainer>
        );
    }
);

TabList.displayName = "TabList";

const useUnderline = (
    tabRef: HTMLElement
): {
    left: number;
    width: number;
} => {
    const [sliderCoords, setSliderCoords] = useState({
        left: 0,
        width: 0,
    });

    useEffect(() => {
        if (tabRef) {
            const left = tabRef.offsetLeft;
            const width = tabRef.offsetWidth;
            setSliderCoords({ left, width });
        }
    }, [tabRef]);

    return sliderCoords;
};
